import { PropsWithChildren, ReactNode, useCallback, useState } from 'react'
import { Icon } from '@mdi/react'
import { mdiAlertCircleOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import { CommonLinkButton } from 'components/Button'
import { SpinnerCentered } from 'components/Loader'
import SuccessModal from 'components/Modal/SuccessModal'
import { commonMessages } from 'app/i18n/messages'
import theme from 'theme_'

interface ConfirmButtonCellProps {
  body: ReactNode
  handleConfirm: () => Promise<void>
  onModalClose?: () => void
  title: ReactNode
  errorMessage?: string | null
  isEnabledErrorModal?: boolean
  disabled?: boolean
}

export function ConfirmButtonCell({
  body,
  children,
  handleConfirm,
  title,
  onModalClose,
  errorMessage,
  isEnabledErrorModal = false,
  disabled = false,
}: PropsWithChildren<ConfirmButtonCellProps>) {
  const { formatMessage } = useIntl()

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleClick = useCallback(async () => {
    try {
      setIsLoading(true)
      await handleConfirm()
      setIsConfirmModalOpen(true)
    } catch (e) {
      setHasError(true)
      setIsErrorModalOpen(true)
    } finally {
      setTimeout(setHasError, 10000, false)
      setIsLoading(false)
    }
  }, [handleConfirm])

  const handleOnConfirm = () => {
    setIsConfirmModalOpen(false)
    if (onModalClose) onModalClose()
  }

  const handleConfirmErrorModal = () => {
    setIsErrorModalOpen(false)
  }

  if (isLoading) return <SpinnerCentered size="24" />

  if (hasError)
    return (
      <>
        <SuccessModal
          title="Cannot Deploy Configuration"
          isOpen={isEnabledErrorModal && isErrorModalOpen}
          confirmBtnTitle={formatMessage(commonMessages.close)}
          onConfirm={handleConfirmErrorModal}
        >
          <div>
            <Icon
              path={mdiAlertCircleOutline}
              size={5}
              color={theme.colors.error}
              title={errorMessage || formatMessage(commonMessages.commonError)}
              description={
                errorMessage || formatMessage(commonMessages.commonError)
              }
            />
          </div>
          {errorMessage || formatMessage(commonMessages.commonError)}
        </SuccessModal>
      </>
    )

  return (
    <>
      <CommonLinkButton disabled={disabled} onClick={handleClick}>
        {children}
      </CommonLinkButton>

      <SuccessModal
        isOpen={isConfirmModalOpen}
        confirmBtnTitle={formatMessage(commonMessages.ok)}
        title={title}
        onConfirm={handleOnConfirm}
      >
        {body}
      </SuccessModal>
    </>
  )
}
